import {
  Check as CheckIcon,
  Close as CloseIcon,
  DeleteForever as DeleteForeverIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Button, CircularProgress, IconButton, TextField, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as React from 'react';

const useStyles = makeStyles((theme) => ({
  button: {
    height: 'fit-content',
  },
  editIcon: {
    opacity: 0.3,
    '&:hover': {
      opacity: 1,
    },
  },
  inputRoot: {
    fontSize: '3rem',
    fontWeight: 400,
    letterSpacing: '0em',
    lineHeight: '1.167',
  },
  deleteButton: {
    color: theme.palette.getContrastText(theme.palette.warning.main),
    backgroundColor: theme.palette.warning.main,
    '&:hover': {
      backgroundColor: theme.palette.warning.dark,
    },
  },
}));

interface NameProps {
  name: string;
  onUpdate: (value: string) => Promise<any>;
  onDelete: () => void;
}

export const Name = ({ name, onUpdate, onDelete }: NameProps): JSX.Element => {
  const classes = useStyles();
  const [localName, setLocalName] = React.useState(name);
  const [isEditing, setIsEditing] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    setLocalName(name);
    setIsEditing(false);
  }, [name]);

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {!isEditing ? (
        <>
          <Typography variant="h4" sx={{ mx: 1 }}>
            {localName}
          </Typography>
          <IconButton
            onClick={() => setIsEditing(true)}
            className={classes.editIcon}
            size="large"
            sx={{ mx: 1 }}
          >
            <EditIcon />
          </IconButton>
          <Button
            className={classes.deleteButton}
            color="warning"
            startIcon={<DeleteForeverIcon sx={{ ml: 1 }} />}
            onClick={onDelete}
            sx={{ mx: 1 }}
          >
            <Typography sx={{ mr: 1 }}>Delete</Typography>
          </Button>
        </>
      ) : (
        <>
          <TextField
            fullWidth={true}
            value={localName}
            onChange={(evt) => setLocalName(evt.target.value)}
            disabled={isSaving}
            margin="dense"
            // TODO: backend OpenAPI does not enforce this length. Add `maxLength: 64` to
            // `Structure.name` in customer-lambdas/doc/webapp.yaml (mirroring structures.name
            // varchar(64) in db_setup.sql), then delete this client-side cap + counter.
            // Today the DB write fails with a cryptic string_data_right_truncation error if
            // the cap is bypassed.
            inputProps={{
              className: classes.inputRoot,
              maxLength: 64,
            }}
            // TODO: backend OpenAPI does not enforce non-empty. Add `minLength: 1` to
            // `Structure.name` in customer-lambdas/doc/webapp.yaml — the DB column is
            // NOT NULL but accepts empty strings, so without this client-side guard a
            // user can save a blank property name (silently breaking navigation labels).
            error={localName.trim() === ''}
            helperText={
              localName.trim() === '' ? 'Property name is required' : `${localName.length}/64`
            }
            FormHelperTextProps={{ sx: { textAlign: 'right', m: 0 } }}
          />
          <IconButton
            disabled={isSaving || localName.trim() === ''}
            onClick={async () => {
              const trimmed = localName.trim();
              setIsSaving(true);
              try {
                await onUpdate(trimmed);
                setLocalName(trimmed);
                setIsEditing(false);
              } catch (err) {
                // Reset to original name on error
                setLocalName(name);
                setIsEditing(false);
              } finally {
                setIsSaving(false);
              }
            }}
            className={classes.button}
            size="large"
          >
            {isSaving ? <CircularProgress size={24} /> : <CheckIcon />}
          </IconButton>
          <IconButton
            disabled={isSaving}
            onClick={() => {
              setLocalName(name);
              setIsEditing(false);
            }}
            className={classes.button}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </>
      )}
    </div>
  );
};
